import React, { useCallback, useEffect } from 'react'
import { actionCreators } from '@/store/ReportSubmissionGroup'
import { Main, MainBody, MainTop, MainTopTitle, WhiteBox } from '@/components/Layout'
import { Col, Row } from 'reactstrap'

import {
  BsrsGrid,
  Column,
  commandColumnProps,
  DateCell,
  DateFilterCell,
  EnumCellStatus,
  EnumFilterCell
} from '@/components/BsrsGrid'
import Resx from '@/components/Resx'
import LoadingIndicator from '@/components/LoadingIndicator'
import context from '@/context'
import { UploadReports } from '@/components/reporting/reportUpload'
import ReportDownloadButtons from '@/components/ReportDownloadButtons'
import ReportAttachments from '@/components/reporting/reportAttachments'
import ReportComments from '@/components/reporting/reportComments'
import {
  reportSubmissionCheckingRuleStatus,
  reportSubmissionsStatus
} from '@/components/reporting/submissions/reportSubmissionsStatus'
import GroupHeader from '@/components/reporting/submissions/groupHeader'
import ErrorBoundary from '@/components/ErrorBoundary'
import { ReportSubmissionsTabs } from '@/components/reporting/submissions/reportSubmissionsTabs'
import { ReportSubmissionState } from '@/enums/ReportSubmissionState'
import { CheckingRuleResult } from '@/enums/checkingRuleResult'
import SubmittedXmls from './xmlSigning/submittedXmls'
import { useLocalization } from '@/hooks/localization'
import { useDispatch } from '@/hooks/dispatch'
import { allowErrorDialog } from '@/utils/fetch'
import SubmitButton from './reportSubmissionsSubmitButton'
import { useSignalR } from '@/hooks/useSignalR'
import { useSelector } from 'react-redux'

function ReportSubmissions (props) {
  const { localize } = useLocalization()

  const {
    reportSubmissionGroupOuterState,
    reportSubmissionGroup,
    previousSubmissionGroupState,
    gridKey,
    fetching,
    attachmentsKey
  } = useSelector(state => {
    return {
      reportSubmissionGroupOuterState: state.reportSubmissionGroup,
      reportSubmissionGroup: state.reportSubmissionGroup.reportSubmissionGroup,
      previousSubmissionGroupState: state.reportSubmissionGroup.previousSubmissionGroupState,
      gridKey: state.reportSubmissionGroup.gridKey,
      fetching: state.reportSubmissionGroup.fetching,
      attachmentsKey: state.reportSubmissionGroup.attachmentsKey
    }
  })
  const [get, setGridKey, addAttachments, removeAttachments, addReportUploads, removeReportUploads, applyReportUpload, reportUploadApplied] =
      useDispatch([
        actionCreators.get,
        actionCreators.setGridKey,
        actionCreators.addAttachments,
        actionCreators.removeAttachments,
        actionCreators.addReportUploads,
        actionCreators.removeReportUploads,
        actionCreators.applyReportUpload,
        actionCreators.reportUploadApplied
      ])

  const parentId = props.match.params.parentId

  useSignalR('checkingRulesFinished', async data => {
    const parsed = JSON.parse(data)
    const id = parseInt(parentId)

    if (parsed.reportInstanceGroupId === id) setGridKey()
  }, [])

  useSignalR('importSavingFinished', () => {
    get(parentId, allowErrorDialog)
    reportUploadApplied()
  }, [])

  const hasReportConstants = reportSubmissionGroup
    ? reportSubmissionGroup.hasReportConstants
    : false

  useEffect(() => {
    get(parentId, allowErrorDialog)
  }, [get, parentId])

  const getBackUrl = useCallback(forecasting => {
    return forecasting ? '/forecast-report-submission-groups' : '/report-submission-groups'
  }, [])

  const renderBody = useCallback(() => {
    const enumName = 'ReportSubmissionState'
    const checkingRuleResultEnumName = CheckingRuleResult.enumName

    return (
      <React.Fragment>
        <WhiteBox
          header='Reporting.Sheets'
          buttons={<React.Fragment>
            <ReportDownloadButtons previousSubmissionGroupState={previousSubmissionGroupState} reportSubmissionGroup={reportSubmissionGroup} />
          </React.Fragment>}
        >
          <BsrsGrid lastReloadedAt={gridKey} defaultSort={[{ field: 'order', dir: 'asc' }]} dataSource={request => context.api.reportSubmissionService.getTableData(parentId, localize('Common.CBI'), reportSubmissionGroup.forecasting, request)}>
            <Column field='order' title={localize('Common.Order')} filter='numeric' editable={false} />
            <Column field='code' title={localize('Common.Code')} editable={false} />
            <Column field='name' title={localize('Common.Name')} editable={false} />
            <Column field='latinName' title={localize('Common.LatinName')} editable={false} />
            <Column field='state' title={localize('Common.Status')} editable={false}
              cell={EnumCellStatus({ localize, enumName, visualStatus: reportSubmissionsStatus })}
              filterCell={EnumFilterCell({ localize, enumName }, ReportSubmissionState.values)} enumName={enumName} />
            <Column field='checkingRuleResult' title={localize('Reporting.CheckingRuleResult')} editable={false}
              cell={EnumCellStatus({ localize, enumName: checkingRuleResultEnumName, visualStatus: reportSubmissionCheckingRuleStatus })}
              filterCell={EnumFilterCell({ localize, enumName: checkingRuleResultEnumName }, CheckingRuleResult.values)} enumName={checkingRuleResultEnumName} />
            <Column field='lastStateModificationTime' title={localize('Common.StatusChangedAt')} filter='date' cell={DateCell} filterCell={DateFilterCell} editable={false} />
            <Column field='lastStateModifierUserName' title={localize('Common.StatusChangedBy')} editable={false} />
            <Column field='numberOfComments' title={localize('Reporting.NumberOfComments')} editable={false} filter='numeric' />

            <Column {...commandColumnProps({ detailUrl: x => `/${reportSubmissionGroup.forecasting ? 'forecast-' : ''}report-submission-groups/${parentId}/report-submissions/${x.id}` })} title=' ' width={150} />
          </BsrsGrid>
          <hr />
          <Row>
            <Col sm='6' />
            <Col sm='6'>
              { reportSubmissionGroup && <SubmitButton submissionGroup={reportSubmissionGroup}
                processState={reportSubmissionGroup.processState} /> }
            </Col>
          </Row>
          <Row>
            <Col>
              <SubmittedXmls
                dataSource={() => context.api.reportSubmissionGroupService.getSubmittedXmls(parentId)}
                downloadAction={context.api.reportSubmissionGroupService.downloadSubmittedXml}
              />
            </Col>
          </Row>
        </WhiteBox>

        <UploadReports
          key={reportSubmissionGroup.reportUploadsKey + 100}
          reportSubmissionGroupState={reportSubmissionGroup.state}
          reportSubmissionGroupProcessState={reportSubmissionGroup.processState}
          dataSource={() => context.api.reportSubmissionGroupService.getReportUploads(parentId)}
          downloadAction={context.api.reportSubmissionGroupService.downloadReportUpload}
          parentId={parentId}
          addReportUploads={addReportUploads}
          removeReportUploads={id => removeReportUploads({ entityId: parentId, fileIds: [id] })}
          applyReportUpload={applyReportUpload}
          adding={reportSubmissionGroupOuterState.reportUploadAdding}
          applying={reportSubmissionGroupOuterState.reportUploadApplying} />

        {hasReportConstants &&
          <ErrorBoundary>
            <WhiteBox header='Common.Constants' collapse >
              <BsrsGrid
                lastReloadedAt={gridKey}
                dataSource={request => context.api.reportSubmissionGroupService.getUsedConstants(request, parentId)}
                defaultSort={[{ field: 'key', dir: 'asc' }]}>
                <Column field='key' title={localize('Common.Name')} editable={false} />
                <Column field='value' title={localize('Common.Value')} editable={false} />
                <Column field='description' title={localize('Common.Description')} editable={false} />
                <Column field='latinDescription' title={localize('Common.LatinDescription')} editable={false} />
                <Column field='validFrom' title={localize('Common.ValidFrom')} cell={DateCell} filterCell={DateFilterCell} filter='date' editable={false} />
                <Column field='validTo' title={localize('Common.ValidTo')} cell={DateCell} filterCell={DateFilterCell} filter='date' editable={false} />
              </BsrsGrid>
            </WhiteBox>
          </ErrorBoundary>}

        <ReportAttachments
          key={attachmentsKey}
          dataSource={() => context.api.reportSubmissionGroupService.getAttachments(parentId)}
          downloadAction={context.api.reportSubmissionGroupService.download}
          parentId={parentId}
          addAttachments={addAttachments}
          removeAttachment={id => removeAttachments({ entityId: parentId, fileIds: [id] })}
          adding={reportSubmissionGroup.adding} />
        <ReportComments parentId={parentId} disabled={false} />
      </React.Fragment>
    )
  }, [addAttachments, addReportUploads, applyReportUpload, attachmentsKey, gridKey, hasReportConstants, localize, parentId, previousSubmissionGroupState, removeAttachments, removeReportUploads, reportSubmissionGroup, reportSubmissionGroupOuterState.reportUploadAdding, reportSubmissionGroupOuterState.reportUploadApplying])

  if (fetching === true || !reportSubmissionGroup) { return <LoadingIndicator /> }
  return (reportSubmissionGroup &&
    <Main>
      <MainTop>
        <MainTopTitle backUrl={getBackUrl(reportSubmissionGroup.forecasting)}>
          <Resx name='Reporting.ReportGroupSubmissionDetailTitle' />
        </MainTopTitle>
      </MainTop>
      <MainBody transparent>
        <ReportSubmissionsTabs parentId={parentId}>
          <ErrorBoundary><GroupHeader reportSubmissionGroup={reportSubmissionGroup} /></ErrorBoundary>
          {renderBody()}
        </ReportSubmissionsTabs>
      </MainBody>
    </Main>
  )
}

export default ReportSubmissions



// WEBPACK FOOTER //
// ./src/report-app/pages/reporting/submissions/reportSubmissions.js